import React, { MouseEventHandler } from 'react'
import { useLocation } from 'react-router-dom'

import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton, {
  BASIC_BUTTON_TYPE_FLAT,
  BASIC_BUTTON_TYPE_OUTLINED,
  type BasicButtonProps,
} from '@divvy-web/skylab.basicbutton'
import { TOAST_TYPE_BASIC, useToast } from '@divvy-web/skylab.toast'

import { useAuth } from '@sputnik/auth'
import { logoutBill, navigateToBillLogin } from '@sputnik/utils/billUtils'
import { logInfo } from '@sputnik/utils/loggerUtils'

interface LogoutButtonProps extends Omit<BasicButtonProps, 'buttonType' | 'type'> {
  bdcOrgId?: string
  dataTestId?: string
  buttonType?: typeof BASIC_BUTTON_TYPE_FLAT | typeof BASIC_BUTTON_TYPE_OUTLINED
}
const LogoutButton = ({
  bdcOrgId,
  buttonType = BASIC_BUTTON_TYPE_FLAT,
  dataTestId = 'LogoutButton',
  onClick,
  ...rest
}: LogoutButtonProps) => {
  const location = useLocation()
  const { logout, resetAuth } = useAuth()
  const showBasicToast = useToast(TOAST_TYPE_BASIC) as any

  const handlePostLogout = () => {
    showBasicToast(
      <FormattedMessage
        defaultMessage='Log out successful!'
        id='sputnik.LogoutButton__VckeyC'
      />,
      {
        autoHideDelay: 5000,
        dataTestId: 'logout-success-toast',
      },
    )
    logInfo('logout', {
      attributes: {
        source: 'LogoutButton',
        result: 'logging user out of the application',
      },
      eventName: 'logout',
    })
  }

  const handleOnClick: MouseEventHandler<HTMLButtonElement> = async (event) => {
    onClick?.(event)
    if (bdcOrgId) {
      await logoutBill(bdcOrgId)
      resetAuth({
        location,
        reason: 'LogoutButtonClicked',
        source: 'LogoutButton',
      })
      handlePostLogout()
      navigateToBillLogin(bdcOrgId)
    } else {
      logout()
      handlePostLogout()
    }
  }

  return (
    <BasicButton
      {...rest}
      className='logout-btn'
      dataTestId={dataTestId}
      name='logout'
      type={buttonType}
      onClick={handleOnClick}
    >
      <FormattedMessage
        defaultMessage='Log out'
        id='sputnik.LogoutButton__PlBReU'
      />
    </BasicButton>
  )
}

export default LogoutButton
